export const chamberMeta = {
    manifoldId: "",
    usedByManifold: true,
};

/**
 * ADS Chamber Class.
 */
export default class Chamber{
    /**
     * Chamber accessors
     * @param {*} x - position in x
     * @param {*} y - position in y
     * @param {*} height
     * @param {*} width
     * @param {*} row - coordinate on chambered on row
     * @param {*} col - coordinate on chambered on column
     * @param {*} active
     * @param {object} accessory - accessory tied to chamber - Manifolds
     * @param {boolean} upperCapActive - flag that ties an upper cap
     * @param {boolean} lowerCapActive - flag that ties an lower cap
     * @param {*} upperCapAccessory - Manifolds tied to chamber
     * @param {*} lowerCapAccessory - Manifolds tied to chamber
     * @param {*} inManifold - not clear
     * @param chamberMetaData
     * @param {[string]} isIsolatorArray - Array of manifold Ids used to control isolator row
     * @param {[string]} isVoidArray - Array of manifold Ids used to mark manifold void pertenece
     * @param {[string]} isManifoldConnectedArray - Array of manifold Ids used to mark manifold or stubs are connected
     * @param {boolean} isOppositeStubAttached - True if a secondary stub is attached
     * @param {string} manifoldSize
     * @param {number} manifoldDirection
     */
    constructor(
        x = 0,
        y = 0,
        height = 0,
        width = 0,
        row = 0,
        col = 0,
        active = false,
        accessory = null,
        upperCapActive = false,
        lowerCapActive = false,
        upperCapAccessory = null,
        lowerCapAccessory = null,
        inManifold = false,
        chamberMetaData = [],
        isIsolatorArray = [],
        isVoidArray = [],
        isManifoldConnectedArray = [],
        isOppositeStubAttached = false,
        manifoldSize = "",
        manifoldDirection = 0,
    ) {
        this._x = x;
        this._y = y;
        this._height = height;
        this._width = width;
        this._row = row;
        this._col = col;
        this._active = active;
        this._accessory = accessory;

        this._upperCapActive = upperCapActive;
        this._lowerCapActive = lowerCapActive;
        this._upperCapAccessory = upperCapAccessory;
        this._lowerCapAccessory = lowerCapAccessory;
        this._inManifold = inManifold;

        this._chamberMetaData = chamberMetaData;
        this._isIsolatorArray = isIsolatorArray;
        this._isVoidArray = isVoidArray;
        this._isManifoldConnectedArray = isManifoldConnectedArray;
        this._isOppositeStubAttached = isOppositeStubAttached;

        this._manifoldSize = manifoldSize;
        this._manifoldDirection = manifoldDirection;
    }

    get chamberMetaData() {
        return this._chamberMetaData;
    }

    set chamberMetaData( value ) {
        this._chamberMetaData = value;
    }

    get isIsolatorArray() {
        return this._isIsolatorArray;
    }

    set isIsolatorArray( value ) {
        this._isIsolatorArray = value;
    }

    get isVoidArray() {
        return this._isVoidArray;
    }

    set isVoidArray( value ) {
        this._isVoidArray = value;
    }

    get isManifoldConnectedArray() {
        return this._isManifoldConnectedArray;
    }

    set isManifoldConnectedArray( value ) {
        this._isManifoldConnectedArray = value;
    }

    get isOppositeStubAttached() {
        return this._isOppositeStubAttached;
    }

    set isOppositeStubAttached( value ) {
        this._isOppositeStubAttached = value;
    }

    get manifoldSize() {
        return this._manifoldSize;
    }

    set manifoldSize( value ) {
        this._manifoldSize = value;
    }

    get manifoldDirection() {
        return this._manifoldDirection;
    }

    set manifoldDirection( value ) {
        this._manifoldDirection = value;
    }

    get x() {
        return this._x;
    }

    set x( value ) {
        this._x = value;
    }

    get y() {
        return this._y;
    }

    set y( value ) {
        this._y = value;
    }

    get height() {
        return this._height;
    }

    set height( value ) {
        this._height = value;
    }

    get width() {
        return this._width;
    }

    set width( value ) {
        this._width = value;
    }

    get row() {
        return this._row;
    }

    set row( value ) {
        this._row = value;
    }

    get col() {
        return this._col;
    }

    set col( value ) {
        this._col = value;
    }

    get active() {
        return this._active;
    }

    set active( value ) {
        this._active = value;
    }

    get accessory() {
        return this._accessory;
    }

    set accessory( value ) {
        this._accessory = value;
    }

    get upperCapActive() {
        return this._upperCapActive;
    }

    set upperCapActive( value ) {
        this._upperCapActive = value;
    }

    get lowerCapActive() {
        return this._lowerCapActive;
    }

    set lowerCapActive( value ) {
        this._lowerCapActive = value;
    }

    get upperCapAccessory() {
        return this._upperCapAccessory;
    }

    set upperCapAccessory( value ) {
        this._upperCapAccessory = value;
    }

    get lowerCapAccessory() {
        return this._lowerCapAccessory;
    }

    set lowerCapAccessory( value ) {
        this._lowerCapAccessory = value;
    }

    get inManifold() {
        return this._inManifold;
    }

    set inManifold( value ) {
        this._inManifold = value;
    }

    /**
     * Get the inlet and outlet counters attached to this chamber.
     */
    getInletOutletCount() {
        const counters = {
            inlet: 0,
            outlet: 0,
        };

        const thisChamber = this;
        if (
            thisChamber.upperCapActive &&
            thisChamber.upperCapAccessory &&
            thisChamber.upperCapAccessory.id
        ) {
            // count inlets
            counters.inlet += thisChamber.upperCapAccessory
                .id
                .toLowerCase()
                .match(/(inlet)/g)
                ? 1
                : 0;

            // count outlets
            counters.outlet += thisChamber.upperCapAccessory.id
                .toLowerCase()
                .match(/(outlet)/g)
                ? 1
                : 0;
        }
        if (
            thisChamber.lowerCapActive &&
            thisChamber.lowerCapAccessory &&
            thisChamber.lowerCapAccessory.id
        ) {
            // count inlets
            counters.inlet += thisChamber.lowerCapAccessory
                .id
                .toLowerCase()
                .match(/(inlet)/g)
                ? 1
                : 0;

            // count outlets
            counters.outlet += thisChamber.lowerCapAccessory.id
                .toLowerCase()
                .match(/(outlet)/g)
                ? 1
                : 0;
        }
        return counters;
    }

    clone() {
        let clone = Object.assign(Object.create(this), this);
        clone._accessory = { ...this._accessory };
        return clone;
    }
}



// WEBPACK FOOTER //
// ./src/lib/chamber.js